import { Stack } from "@mantine/core";
import { pick } from "lodash";
import { useTranslation } from "react-i18next";

import { CreateGarageForm } from "@/components/forms";
import { useUpdateGarage } from "@/services/garages/update-garage";
import { showCustomNotification } from "@/utils/notifications";

import type { UpdateGarageInput } from "@/services/garages/update-garage";
import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type UpdateGarageProps = {
  autoCloseDelay?: boolean | number;
  garage: Garage;
  onClose?: () => void;
  onSuccess?: (garageId: string) => void;
} & MantineStyleProps;

export const UpdateGarage: FC<UpdateGarageProps> = ({
  autoCloseDelay = false,
  garage,
  onClose,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useUpdateGarage({
    mutationConfig: { onSuccess: handleSuccess },
  });
  const initialValues = pick(garage.data, ["name"]) as UpdateGarageInput;

  function handleSuccess(data: Garage) {
    showCustomNotification({
      title: t("components.UpdateGarage.success.title"),
      message: t("components.UpdateGarage.success.message"),
      autoClose: autoCloseDelay,
    });
    onClose?.();
    onSuccess?.(data.identifier);
  }

  function handleSubmit(data: UpdateGarageInput) {
    mutate({ garageId: garage.identifier, data });
  }

  return (
    <Stack {...props}>
      <CreateGarageForm
        initialValues={initialValues}
        labels={{ submitButton: t("components.UpdateGarage.submitButton") }}
        loading={isPending}
        onCancel={onClose}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
};
